import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMerch } from '../../contexts/MerchContext';
import { supabase } from '../../utils/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader, ArrowLeft, Check, AlertTriangle, Truck } from 'lucide-react';
// Reuse existing stripe promise from utils
import { stripePromise } from '../../utils/stripeService';

const CheckoutForm = ({ clientSecret, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setProcessing(true);

        // Validate form first
        const { error: submitError } = await elements.submit();
        if (submitError) {
            setError(submitError.message);
            setProcessing(false);
            return;
        }

        const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
                return_url: `${window.location.origin}/merch/success`,
            },
            redirect: "if_required" // Try to handle without redirect if possible for smoother UX
        });

        if (paymentError) {
            setError(paymentError.message);
            setProcessing(false);
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            onSuccess(paymentIntent);
        } else {
            // e.g. requires action
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <PaymentElement />
            {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center text-red-500 text-sm">
                    <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                    {error}
                </div>
            )}
            <button
                disabled={!stripe || processing}
                className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
                {processing ? <Loader className="w-5 h-5 animate-spin" /> : 'Pay Now'}
            </button>
        </form>
    );
};

const Checkout = () => {
    const navigate = useNavigate();
    const { user, userProfile } = useAuth(); // Use global auth context
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [address, setAddress] = useState({
        name: '',
        address1: '',
        city: '',
        state_code: '',
        country_code: 'US', // default
        zip: '',
        email: '',
        phone: ''
    });

    // Enforce Login
    useEffect(() => {
        if (!user) {
            // Redirect to login (assuming explicit login page or handled by guard, but let's be safe)
            // Ideally prompt login modal, but for now redirect
            const returnUrl = encodeURIComponent('/merch/checkout');
            navigate(`/login?returnUrl=${returnUrl}`);
            return;
        }

        // Auto-populate from Profile or User Auth
        if (user) {
            setAddress(prev => ({
                ...prev,
                email: user.email || prev.email, // Auto-fill email
                // Pre-fill from profile if available (will be added to DB schema later)
                name: userProfile?.shipping_info?.name || userProfile?.full_name || prev.name,
                address1: userProfile?.shipping_info?.address1 || prev.address1,
                city: userProfile?.shipping_info?.city || prev.city,
                state_code: userProfile?.shipping_info?.state_code || prev.state_code,
                country_code: userProfile?.shipping_info?.country_code || prev.country_code,
                zip: userProfile?.shipping_info?.zip || prev.zip,
                phone: userProfile?.shipping_info?.phone || prev.phone
            }));
        }
    }, [user, userProfile, navigate]);

    if (!user) return null; // Prevent render while redirecting

    if (cart.length === 0) {
        return (
            <div className="min-h-screen bg-background pt-20 flex flex-col items-center justify-center p-4">
                <h2 className="text-2xl font-bold text-foreground mb-4">Your cart is empty</h2>
                <button
                    onClick={() => navigate('/merch')}
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg"
                >
                    Go Shopping
                </button>
            </div>
        );
    }

    const handleAddressSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Fetch Shipping Rates
            // Need structured items for Printful: { quantity, variant_id }
            // Assuming 'merch-api' accepts { items: [...], recipient: address }
            // My `merch-api` proxy expects Printful format or I adapt it. 
            // Printful `calculate-shipping` expects: { recipient: {...}, items: [...] }

            const { data: { session } } = await supabase.auth.getSession();

            const payload = {
                recipient: {
                    ...address,
                    country_code: address.country_code
                },
                items: cart.map(i => ({
                    variant_id: i.catalog_variant_id || i.variant_id,
                    quantity: i.quantity
                }))
            };

            const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/merch-api/calculate-shipping`;
            const res = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await res.json();
            if (result.code === 200 && result.result) {
                setRates(result.result);
                if (result.result.length > 0) setSelectedRate(result.result[0]); // Auto select first
                setStep(2);

                // Start Background Save of Address
                const { error: updateError } = await supabase
                    .from('user_profiles')
                    .update({ shipping_info: address })
                    .eq('id', user.id);

                if (updateError) console.error('Failed to save shipping info:', updateError);

            } else {
                console.error('Shipping Calculation Error:', result);
                alert(`Could not calculate shipping: ${result.result || result.error || 'Check address details'}`);
            }
        } catch (e) {
            console.error(e);
            alert('Error calculating shipping');
        } finally {
            setLoading(false);
        }
    };

    const handleShippingSelect = async () => {
        if (!selectedRate) return;
        setLoading(true);
        try {
            // Create Payment Intent
            const { data: { session } } = await supabase.auth.getSession();

            // Total calculation: Cart Total + Shipping Rate
            // NOTE: Secure way is backend recalculation.
            const shippingCost = parseFloat(selectedRate.rate);
            const totalAmount = Math.round((cartTotal + shippingCost) * 100); // in cents

            // Normalize items to ensure correct variant_id is sent (matching calculate-shipping logic)
            const normalizedItems = cart.map(i => ({
                ...i,
                product_id: i.product_id, // Ensure product_id is passed
                variant_id: i.catalog_variant_id || i.variant_id, // CRITICAL FIX: Use catalog ID
                quantity: i.quantity,
                name: i.name,
                retail_price: i.retail_price,
                thumbnail_url: i.thumbnail_url
            }));

            const payload = {
                items: normalizedItems,
                shippingAddress: address,
                shippingRate: selectedRate,
                totalAmount
            };

            const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/merch-api/create-checkout-session`;
            const res = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await res.json();
            if (result.clientSecret) {
                setClientSecret(result.clientSecret);
                setStep(3);
            } else {
                console.error('Payment Init Error:', result);
                alert(`Failed to initialize payment: ${result.error || result.message || 'Unknown error'}`);
            }
        } catch (e) {
            console.error(e);
            alert('Error initializing payment');
        } finally {
            setLoading(false);
        }
    }

    const handlePaymentSuccess = (paymentIntent) => {
        clearCart();
        // Redirect to custom success page
        // Redirect to custom success page
        navigate('/merch/success');
        alert('Payment Successful! Order placed.');
    };

    return (
        <div className="min-h-screen bg-background pt-20 pb-12">
            <div className="container mx-auto px-4 max-w-3xl">
                <button onClick={() => navigate('/merch')} className="flex items-center text-muted-foreground mb-8">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Cart
                </button>

                <h1 className="text-3xl font-bold text-foreground mb-8">Checkout</h1>

                <div className="bg-card border border-border rounded-xl p-6 md:p-8">
                    {/* Step 1: Address */}
                    {step === 1 && (
                        <form onSubmit={handleAddressSubmit}>
                            <h2 className="text-xl font-semibold mb-4">Shipping Address</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <input required placeholder="Full Name" className="p-3 rounded-lg bg-input border border-border"
                                    value={address.name} onChange={e => setAddress({ ...address, name: e.target.value })} />
                                <input required placeholder="Email" type="email" className="p-3 rounded-lg bg-input border border-border"
                                    value={address.email} onChange={e => setAddress({ ...address, email: e.target.value })} />
                                <input required placeholder="Address Line 1" className="p-3 rounded-lg bg-input border border-border col-span-2"
                                    value={address.address1} onChange={e => setAddress({ ...address, address1: e.target.value })} />
                                <input required placeholder="City" className="p-3 rounded-lg bg-input border border-border"
                                    value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} />
                                <input required placeholder="State / Province Code (e.g., CA)" className="p-3 rounded-lg bg-input border border-border"
                                    value={address.state_code} onChange={e => setAddress({ ...address, state_code: e.target.value })} />
                                <input required placeholder="Zip / Postal Code" className="p-3 rounded-lg bg-input border border-border"
                                    value={address.zip} onChange={e => setAddress({ ...address, zip: e.target.value })} />
                                <input required placeholder="Country Code (e.g., US)" className="p-3 rounded-lg bg-input border border-border"
                                    value={address.country_code} onChange={e => setAddress({ ...address, country_code: e.target.value })} />
                                <input placeholder="Phone (Optional)" className="p-3 rounded-lg bg-input border border-border"
                                    value={address.phone} onChange={e => setAddress({ ...address, phone: e.target.value })} />
                            </div>
                            <button disabled={loading} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-bold">
                                {loading ? 'Calculating...' : 'Continue to Shipping'}
                            </button>
                        </form>
                    )}

                    {/* Step 2: Shipping */}
                    {step === 2 && (
                        <div>
                            <h2 className="text-xl font-semibold mb-4">Select Shipping Method</h2>
                            <div className="space-y-4 mb-6">
                                {rates.map(rate => (
                                    <div
                                        key={rate.id}
                                        onClick={() => setSelectedRate(rate)}
                                        className={`p-4 border rounded-lg cursor-pointer flex justify-between items-center transition-all ${selectedRate?.id === rate.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/50'
                                            }`}
                                    >
                                        <div className="flex items-center">
                                            <Truck className="w-5 h-5 mr-3 text-muted-foreground" />
                                            <div>
                                                <p className="font-semibold text-foreground">{rate.name}</p>
                                                <p className="text-sm text-muted-foreground">Est. Delivery: {rate.minDeliveryDays}-{rate.maxDeliveryDays} days</p>
                                            </div>
                                        </div>
                                        <div className="font-bold text-foreground">
                                            ${parseFloat(rate.rate).toFixed(2)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-between text-lg font-bold border-t border-border pt-4 mb-6">
                                <span>Total (Cart + Shipping)</span>
                                <span>${(cartTotal + parseFloat(selectedRate?.rate || 0)).toFixed(2)}</span>
                            </div>
                            <button onClick={handleShippingSelect} disabled={loading} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-bold">
                                {loading ? 'Initializing Payment...' : 'Proceed to Payment'}
                            </button>
                        </div>
                    )}

                    {/* Step 3: Payment */}
                    {step === 3 && clientSecret && (
                        <div>
                            <h2 className="text-xl font-semibold mb-6">Payment Details</h2>
                            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
                                <CheckoutForm clientSecret={clientSecret} onSuccess={handlePaymentSuccess} />
                            </Elements>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Checkout;
